import type { Team } from "../team";
import {
  AdvancedFlow,
  SkipCondition,
  TriggerStep,
} from "./advanced-flow-db-parser";
import { RechargeIntegrationTriggerKey } from "./integration-triggers";
import { Integration } from "./integrations";
import { ConditionDataSource, StepType } from "./steps";
import {
  categories,
  isOrderTrackingTrigger,
  isReturnTrackingTrigger,
  MarketingTriggerKey,
  OmsTriggerKey,
  TriggerKey,
  type Category,
} from "./triggers";

export const AUTOMATIONS_TABLE_CATEGORIES = [
  "Order tracking",
  "Return tracking",
  "Try first",
  "Marketing",
  "Reviews",
  "Extended warranty",
  "Integration",
  "Custom Event",
  "Rules",
  "Recover",
] as const satisfies Category[];

export const EXCLUDED_CATEGORIES = categories.filter(
  (c) => !(AUTOMATIONS_TABLE_CATEGORIES as readonly string[]).includes(c),
);

export function getEnabledCategoriesForAutomationsFilter(
  team: Team,
  enabledIntegrations: Record<Integration, boolean>,
): Category[] {
  const hasAnyIntegration = Object.values(enabledIntegrations).some(Boolean);
  const enabledCategories = new Set<Category>();

  if (team.settings.marketing?.enabled) {
    // If marketing is enabled, include marketing AND order tracking regardless of order tracking setting
    enabledCategories.add("Marketing");
    enabledCategories.add("Order tracking");
    enabledCategories.add("Custom Event");

    if (hasAnyIntegration) {
      enabledCategories.add("Integration");
    }
  } else if (team.settings.orderTracking?.enabled) {
    // If only order tracking is enabled (but not marketing), show just order tracking
    enabledCategories.add("Order tracking");
  }

  if (team.settings.returns?.enabled) {
    enabledCategories.add("Return tracking");
  }

  if (team.settings.tnpl?.enabled) {
    enabledCategories.add("Try first");
  }

  if (team.settings.reviews?.enabled) {
    enabledCategories.add("Reviews");
  }

  return Array.from(enabledCategories);
}

export function getTriggerStep(flow: AdvancedFlow): TriggerStep {
  const triggerStep = flow.steps.find((step) => step.type === StepType.TRIGGER);
  if (!triggerStep) {
    throw new Error("No trigger step found in flow");
  }
  return triggerStep;
}

export function shouldSkipSmartSendingForTriggerKey(
  triggerKey: TriggerKey,
): boolean {
  if (
    isOrderTrackingTrigger(triggerKey) ||
    isReturnTrackingTrigger(triggerKey)
  ) {
    return true;
  } else if (
    [
      MarketingTriggerKey.MARKETING_CAMPAIGN,
      MarketingTriggerKey.EMAIL_SIGNUP,
      MarketingTriggerKey.SMS_SIGNUP,
      MarketingTriggerKey.BACK_IN_STOCK,
      RechargeIntegrationTriggerKey.UPCOMING_CHARGE,
      OmsTriggerKey.ORDER_READY_FOR_PICKUP,
    ].includes(triggerKey as MarketingTriggerKey)
  ) {
    return true;
  }

  return false;
}

export function isTexasSkipCondition({
  condition,
  texasSegmentId,
}: {
  condition: SkipCondition;
  texasSegmentId: string;
}) {
  return (
    (!condition.actionTypes ||
      condition.actionTypes.includes(StepType.SEND_SMS)) &&
    condition.dataSource === ConditionDataSource.ExistingSegment &&
    condition.segmentIds.includes(texasSegmentId)
  );
}

export function doesFlowHaveTexasSegmentExclusion({
  steps,
  texasSegmentId,
}: {
  steps: AdvancedFlow["steps"];
  texasSegmentId: string;
}) {
  const triggerStep = steps.find((step) => step.type === StepType.TRIGGER);
  if (!triggerStep) {
    return false;
  }
  return triggerStep.skipConditions?.conditions.some((condition) =>
    isTexasSkipCondition({ condition, texasSegmentId }),
  );
}
